/**
 * The shared master bus. One AudioContext for the whole instrument; every scene
 * engine is handed the bus input as a plain AudioNode and never touches
 * `ctx.destination` itself (architecture.md invariant: engines accept an output
 * node). The recorder taps the same bus, so what you export is what you heard.
 */

export interface MasterBus {
  readonly ctx: AudioContext;
  /** Connect scene engines here. */
  readonly input: GainNode;
  /** Post-compressor node — the recorder tap and the analyser hang off this. */
  readonly tap: GainNode;
  readonly analyser: AnalyserNode;
  setLevel(v: number): void;
  /** Fast fade and recover — the hush. Ringing voices die under it. */
  duck(): void;
}

let bus: MasterBus | null = null;

export function isStarted(): boolean {
  return bus !== null;
}

export function getBus(): MasterBus {
  if (!bus) throw new Error('audio not started — the start gate has not been passed');
  return bus;
}

/**
 * Must be called from a user gesture. iOS Safari will construct a suspended
 * context otherwise and every scene will look broken rather than silent.
 */
export async function startAudio(): Promise<MasterBus> {
  if (bus) {
    if (bus.ctx.state === 'suspended') await bus.ctx.resume();
    return bus;
  }
  const Ctor: typeof AudioContext =
    window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new Ctor({ latencyHint: 'interactive' });

  // iOS resolves a Web-Audio-only page to the 'ambient' audio session, which the
  // hardware Ring/Silent switch mutes. A phone on silent would then look like a
  // dead instrument while the level meter kept moving, because the analyser sees
  // signal whether or not the output is audible. 'playback' opts out.
  //
  // Wrapped: on an engine shipping a partial AudioSession where `type` is
  // getter-only, a bare assignment throws in strict mode and would turn a
  // silence bug into "Could not start audio" at the gate.
  try {
    const session = (navigator as unknown as { audioSession?: { type: string } }).audioSession;
    if (session) session.type = 'playback';
  } catch { /* no AudioSession, or read-only — ambient it is */ }

  const input = ctx.createGain();
  input.gain.value = 0.9;
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -10;
  comp.knee.value = 14;
  comp.ratio.value = 4;
  comp.attack.value = 0.003;
  comp.release.value = 0.16;
  const tap = ctx.createGain();
  tap.gain.value = 1;
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  analyser.smoothingTimeConstant = 0.75;

  input.connect(comp);
  comp.connect(tap);
  tap.connect(analyser);
  tap.connect(ctx.destination);

  let levelNow = 0.9;
  bus = {
    ctx,
    input,
    tap,
    analyser,
    setLevel(v) {
      levelNow = v * v; // perceptual taper
      input.gain.setTargetAtTime(levelNow, ctx.currentTime, 0.02);
    },
    duck() {
      const now = ctx.currentTime;
      input.gain.cancelScheduledValues(now);
      input.gain.setTargetAtTime(0.0001, now, 0.03);
      input.gain.setTargetAtTime(levelNow, now + 0.3, 0.05);
    },
  };
  // iOS suspends or interrupts the context when the tab is backgrounded, on a
  // call or Siri, and on screen lock — and it does not come back by itself. The
  // instrument then looks alive (meter, tracking, UI) and is silent, which
  // reads as a bug and costs a reload. Very likely during the multi-megabyte
  // model download, so this is registered once, here.
  //
  // Three paths back, because no single event covers every exit:
  // - visibilitychange: backgrounded and returned. Checks !== 'running', not
  //   === 'suspended', because iOS reports a non-standard 'interrupted' state
  //   that the narrower check silently ignored.
  // - statechange: a call or Siri banner can interrupt and end with the page
  //   visible the whole time — visibilitychange never fires for that.
  // - one-shot pointerdown: a resume() during a live interruption is refused;
  //   the next tap is the guaranteed unlock, so arm one when resume may have
  //   failed.
  const tryResume = (): void => {
    if (ctx.state !== 'running') void ctx.resume().catch(() => { /* next path gets it */ });
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tryResume();
  });
  ctx.addEventListener('statechange', () => {
    if (ctx.state !== 'running' && document.visibilityState === 'visible') {
      tryResume();
      window.addEventListener('pointerdown', tryResume, { once: true });
    }
  });

  if (ctx.state === 'suspended') await ctx.resume();
  return bus;
}

/** Reported output latency, in ms — `outputLatency` where Safari has it. */
export function outputLatencyMs(ctx: AudioContext): number {
  const l = (ctx as AudioContext & { outputLatency?: number }).outputLatency;
  return Math.round(((l ?? ctx.baseLatency ?? 0) as number) * 1000);
}
